import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, CheckCheck, Save } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api } from "../../lib/api";

const VAL_AR = { male: "ذكر", female: "أنثى", single: "أعزب", married: "متزوج", divorced: "مطلق", widowed: "أرمل" };
const show = (v) => VAL_AR[v] || v || "—";
const Stat = ({ label, value, tone, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2" data-testid={testid}>
    <p className="text-[11px] text-slate-500">{label}</p>
    <p className={`font-heading text-lg font-bold ${tone || "text-slate-800"}`}>{value}</p>
  </div>
);

export const SheetReconcile = ({ onResult, onSaved }) => {
  const [insurer, setInsurer] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [pick, setPick] = useState({});
  const [saveIns, setSaveIns] = useState(true);
  const [pStart, setPStart] = useState("");
  const [pEnd, setPEnd] = useState("");
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);

  const onFile = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { setFile(f); run(f); } };
  const run = async (f) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("insurer", insurer);
      const r = await api.insuranceSheetReconcile(fd);
      setRes(r); onResult?.(r);
      const p = {}; r.matched.forEach((m) => m.diffs.forEach((d) => { p[`${m.emp_id}|${d.field}`] = true; }));
      setPick(p);
      toast.success(`تمت قراءة ${r.total_rows} عضو — مطابقة ${r.matched.length} موظف`);
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر تحليل الشيت"); }
    finally { setBusy(false); }
  };

  const setAll = (v) => { const p = {}; Object.keys(pick).forEach((k) => { p[k] = v; }); setPick(p); };

  const save = async () => {
    setSaving(true);
    try {
      const items = res.matched.map((m) => {
        const fields = {}; m.diffs.forEach((d) => { if (pick[`${m.emp_id}|${d.field}`]) fields[d.field] = d.sheet; });
        const s = m.sheet;
        const insurance = saveIns ? { name: s.name, member_no: s.member_no, class: s.class, relation: s.relation, sponsor_id: s.sponsor_id, premium: s.premium, start_date: s.start_date, expiry_date: s.expiry_date } : {};
        return { emp_id: m.emp_id, fields, insurance };
      });
      const r = await api.insuranceSheetApply({ insurer: insurer || res.insurer, policy_start: pStart, policy_end: pEnd, items });
      toast.success(`تم تحديث ${r.updated} موظف`);
      onSaved?.();
      if (file) run(file);
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="sheet-reconcile">
      <p className="text-xs text-slate-500">ارفع كشف الأعضاء من شركة التأمين (بأي شكل) — الذكاء الاصطناعي يتعرف على الأعمدة، يطابق برقم الإقامة، ويطلع لك أي اختلاف يسبب الرفض.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input value={insurer} onChange={(e) => setInsurer(e.target.value)} placeholder="اسم شركة التأمين (اختياري)" className="max-w-xs" data-testid="sheet-insurer-input" />
        <input type="file" accept=".xlsx,.xls,.csv" id="ins-sheet-rec" className="hidden" onChange={onFile} data-testid="sheet-reconcile-input" />
        <Button disabled={busy} onClick={() => document.getElementById("ins-sheet-rec").click()} className="gap-1.5 bg-teal-600 hover:bg-teal-700" data-testid="sheet-reconcile-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {busy ? "جاري التحليل..." : "رفع شيت شركة التأمين"}
        </Button>
      </div>
      {res && <>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <Stat label="أعضاء في الشيت" value={res.total_rows} testid="rec-total" />
          <Stat label="مطابقين" value={res.matched.length} tone="text-teal-600" testid="rec-matched" />
          <Stat label="فيهم اختلاف" value={res.with_diffs} tone="text-amber-600" testid="rec-diffs" />
          <Stat label="غير موجود بالنظام" value={res.unmatched.length} tone="text-rose-600" testid="rec-unmatched" />
          <Stat label="موظفين غير مؤمَّنين" value={res.not_in_sheet.length} tone="text-rose-600" testid="rec-not-in-sheet" />
        </div>
        <DiffTable res={res} pick={pick} setPick={setPick} />
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
          <Button variant="outline" size="sm" onClick={() => setAll(true)} className="gap-1" data-testid="rec-select-all"><CheckCheck className="h-4 w-4" /> اعتماد كل قيم شيت التأمين</Button>
          <Button variant="outline" size="sm" onClick={() => setAll(false)} data-testid="rec-select-none">إلغاء التحديد</Button>
          <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={saveIns} onChange={(e) => setSaveIns(e.target.checked)} data-testid="rec-save-premium" /> حفظ القسط والفئة ورقم العضوية لكل موظف (الإجمالي {res.total_premium} ر.س)</label>
          <div className="flex w-full flex-wrap items-center gap-2 rounded-xl bg-teal-50/60 p-2 text-xs text-slate-700">
            <span className="font-semibold">تواريخ البوليصة:</span>
            <label className="flex items-center gap-1">البداية <input type="date" value={pStart} onChange={(e) => setPStart(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1" data-testid="rec-policy-start" /></label>
            <label className="flex items-center gap-1">النهاية <input type="date" value={pEnd} onChange={(e) => setPEnd(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1" data-testid="rec-policy-end" /></label>
            <span className="text-slate-500">النهاية تلقائياً = البداية + سنة. إذا تركتها فاضية يتسجّل الموظف «مسجّل لدى التأمين» بدون تاريخ.</span>
          </div>
          <Button disabled={saving} onClick={save} className="ms-auto gap-1.5 bg-teal-600 hover:bg-teal-700" data-testid="rec-apply-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ في ملفات الموظفين ({res.matched.length})
          </Button>
        </div>
        <Lists res={res} />
      </>}
    </div>
  );
};

const DiffTable = ({ res, pick, setPick }) => {
  const rows = res.matched.filter((m) => m.diffs.length);
  if (!rows.length) return <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="rec-no-diffs">كل البيانات مطابقة — ما فيه أي اختلاف</div>;
  return (
    <div className="max-h-[45vh] overflow-auto rounded-xl border border-slate-200">
      <table className="w-full text-sm" data-testid="rec-diff-table">
        <thead className="sticky top-0 bg-slate-50 text-xs text-slate-500"><tr>
          <th className="px-2 py-2 text-right">الموظف</th><th className="px-2 py-2">الحقل</th><th className="px-2 py-2">في النظام</th><th className="px-2 py-2">في شيت التأمين</th><th className="px-2 py-2">اعتماد</th>
        </tr></thead>
        <tbody>{rows.map((m) => m.diffs.map((d, i) => {
          const k = `${m.emp_id}|${d.field}`;
          return (
            <tr key={k} className="border-t border-slate-100" data-testid={`rec-diff-${m.emp_id}-${d.field}`}>
              {i === 0 && <td rowSpan={m.diffs.length} className="px-2 py-1.5 align-top font-semibold text-slate-800">{m.emp_name}<span className="mt-0.5 block font-mono-nums text-[11px] font-normal text-slate-400" dir="ltr">{m.sheet.iqama_number}</span></td>}
              <td className="px-2 py-1.5 text-center text-slate-600">{d.label}</td>
              <td className="px-2 py-1.5 text-center text-rose-600" dir="auto">{show(d.system)}</td>
              <td className="px-2 py-1.5 text-center text-teal-700" dir="auto">{show(d.sheet)}</td>
              <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={!!pick[k]} onChange={(e) => setPick({ ...pick, [k]: e.target.checked })} data-testid={`rec-pick-${m.emp_id}-${d.field}`} /></td>
            </tr>
          );
        }))}</tbody>
      </table>
    </div>
  );
};

const Lists = ({ res }) => (
  <div className="grid gap-2 sm:grid-cols-2 text-xs">
    {res.unmatched.length > 0 && <details className="rounded-xl border border-rose-100 bg-rose-50/50 p-2" data-testid="rec-unmatched-list">
      <summary className="cursor-pointer font-semibold text-rose-700">مؤمَّن عليهم وغير موجودين بالنظام ({res.unmatched.length})</summary>
      <ul className="mt-1 max-h-40 space-y-0.5 overflow-auto">{res.unmatched.map((u) => <li key={u.iqama_number} dir="ltr" className="text-slate-600">{u.iqama_number} — {u.name}</li>)}</ul>
    </details>}
    {res.not_in_sheet.length > 0 && <details className="rounded-xl border border-amber-100 bg-amber-50/50 p-2" data-testid="rec-missing-list">
      <summary className="cursor-pointer font-semibold text-amber-700">موظفين غير موجودين في شيت التأمين ({res.not_in_sheet.length})</summary>
      <ul className="mt-1 max-h-40 space-y-0.5 overflow-auto">{res.not_in_sheet.map((u) => <li key={u.emp_id} className="text-slate-600">{u.name} <span dir="ltr" className="text-slate-400">{u.iqama_number}</span></li>)}</ul>
    </details>}
  </div>
);
